// THE single resolver for "which backend is this console talking to" — ported from
// tkt-back-office src/lib/backendUrl.js. The REST helpers (lib/api.js) and the better-auth client
// (lib/authClient.js) both read it, so they can never point at different hosts: the session
// cookie lives on the host we authenticated against, and a better-auth call sent anywhere else
// arrives without it and 401s.
//
// Derivation is generic: <anything>.<domain> → backend.<domain>. ops.momoticketing.com talks to
// backend.momoticketing.com — same site, so the session cookie (Domain=.momoticketing.com) rides
// every request. Local and preview hosts have no backend.<domain> sibling and use VITE_BACKEND_URL.

const isLocalOrPreviewHost = (hostname) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) ||
  hostname.endsWith('.local') ||
  hostname.endsWith('.workers.dev');

/** "<sub>.<domain>" for the domain this console is served on, or `fallback` on local/preview hosts. */
export const derivedHostUrl = (sub, fallback) => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0];
    if (!isLocalOrPreviewHost(hostname)) {
      const parts = hostname.split('.');
      const domain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
      return `${protocol}//${sub}.${domain}`;
    }
  }
  return typeof fallback === 'function' ? fallback() : fallback;
};

export const getBackendUrl = () =>
  derivedHostUrl('backend', () => import.meta.env.VITE_BACKEND_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

export const isLocalBackend = () => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(getBackendUrl());

/**
 * THE GATE SCANNER FOR THIS DEPLOYMENT (~/ac-scanner-native-app, one app built per brand).
 * momoticketing.com is the `moments` backend, whose scanner flavour answers on scanner.moments.ug
 * with the `momentsscanner://` deep link (tkt-back-office SCANNER_BY_APEX, measured 2026-10-12).
 * NULL on a local/preview host: a device code minted on a local backend handed to a production
 * scanner would send a steward to a server that has never seen it.
 */
const SCANNER_BY_APEX = {
  'momoticketing.com': { flavor: 'moments', apex: 'moments.ug', name: 'Ticket Scanner', scheme: 'momentsscanner' },
  'moments.ug': { flavor: 'moments', apex: 'moments.ug', name: 'Ticket Scanner', scheme: 'momentsscanner' },
};
export const getScannerApp = () => {
  const hostname = typeof window !== 'undefined' ? window.location?.hostname || '' : '';
  if (!hostname || isLocalOrPreviewHost(hostname)) return null;
  const hit = SCANNER_BY_APEX[hostname.split('.').slice(1).join('.')];
  if (!hit) return null;
  return {
    ...hit,
    webUrl: `https://scanner.${hit.apex}`,
    apkUrl: `https://media.ticket.ug/scanner-apps/${hit.flavor}-scanner.apk`,
  };
};
